import React, { useContext, useEffect, useRef, useState } from 'react';
import * as S from './styled';
import { useSnackbar } from 'notistack';
import {
  defaultBlurDepositForm,
  defaultDepositForm,
} from '../../../../store/invoice/utils';
import Form from '../..';
import { ExportContext } from '../../../../provider/ExportProvider';
import SVG from '../../../SVG';
import { maskFieldDate, unmaskFieldDate } from '../utils';
import { formatBRL, formatCents } from '../../../../store/masks';

export default function FormDepositCreate() {
  const {
    getInvoice,
    setInvoice,
    setModal,
    filterSelectCompanies,
    setCompanyFilterField,
    selectCompanies,
    createDeposits,
    filterDeposit,
    getDepositsFormMessage,
    validateDepositsForm,
    filterDailyDeposit,
    depositGroupItems,
    depositGroups,
    groups,
    selectedGroupId,
  } = useContext(ExportContext)!.controllers.deposit;

  const { enqueueSnackbar } = useSnackbar();

  const forms = getInvoice(`forms.deposits.createMany`);

  const getForm =
    (index: number) =>
      <T extends FlattenKeys<DepositForm>>(field: T) =>
        getInvoice(`forms.deposits.createMany.${index}.${field}`);

  const setForm =
    (index: number) =>
      <
        T extends FlattenKeys<DepositForm>,
        V extends DeepType<
          InvoiceState,
          `forms.deposits.createMany.${number}.${T}`
        >,
      >(
        field: T,
        value: V,
      ) =>
        setInvoice(`forms.deposits.createMany.${index}.${field}`, value);

  const blurField = (index: number) => (field: keyof DepositForm) => () => {
    setInvoice(`blur.deposits.createMany.${index}.${field}`, true);
  };
  const depositGroup = groups.deposit[0];

  const clearForm = () => {
    setInvoice(`forms.deposits.createMany`, [{
      ...defaultDepositForm(),
      group: {
        id: depositGroup?.id ?? 0,
        number: depositGroup?.number ?? 0,
        name: depositGroup?.name ?? ''
      },
    }]);
    setInvoice(`blur.deposits.createMany`, [defaultBlurDepositForm()]);
  };

  const messages = getDepositsFormMessage();

  const [loading, setLoading] = React.useState(false);
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateDepositsForm()
      .then(() => {
        setLoading(true);
        createDeposits()
          .then(() => {
            filterDeposit();
            enqueueSnackbar('Depósito criado com sucesso', {
              variant: 'success',
            });
            clearForm();
            setModal('single.createDeposit', false);
            filterDailyDeposit();
          })
          // .catch(() => {
          //   enqueueSnackbar('Houve uma falha ao criar o depósito', {
          //     variant: 'error',
          //   });
          // })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        enqueueSnackbar('Verifique a validade dos campos', {
          variant: 'error',
        });
      });
  };

  const bodyRef = useRef<HTMLDivElement>(null);

  const [scroll, setScroll] = useState(false);

  const addForm = (index: number) => {
    const blurs = getInvoice(`blur.deposits.createMany`);
    setInvoice(`forms.deposits.createMany`, [...forms, forms[index]]);
    setInvoice(`blur.deposits.createMany`, [
      ...blurs,
      defaultBlurDepositForm(),
    ]);
    setScroll(true);
  };

  useEffect(() => {
    if (scroll && bodyRef.current) {
      bodyRef.current.scrollTo({
        top: bodyRef.current.scrollHeight,
        behavior: 'smooth',
      });
      setScroll(false);
    }
  }, [scroll]);

  const removeForm = (index: number) => {
    const blurs = getInvoice(`blur.deposits.createMany`);
    setInvoice(
      `forms.deposits.createMany`,
      forms.filter((_, i) => index !== i),
    );
    setInvoice(
      `blur.deposits.createMany`,
      blurs.filter((_, i) => index !== i),
    );
  };

  const [companyLoadings, setCompanyLoadings] = useState(
    forms.map(() => false),
  );

  useEffect(() => {
    setCompanyLoadings(forms.map(() => false));
  }, [forms.length]);

  const filterCompanies = (index: number) => {
    setCompanyLoadings(companyLoadings.map((v, i) => (i === index ? true : v)));
    filterSelectCompanies().finally(() => {
      setCompanyLoadings(
        companyLoadings.map((v, i) => (i === index ? false : v)),
      );
    });
  };

  const group = (index: number) => getForm(index)('group')

  useEffect(() => {
    if (!selectedGroupId) {
      const depositGroup = depositGroups[0];
      for (let i = 0; i < forms.length; i++) {
        if (!group(i).id || !depositGroups.some(({ id }) => id == group(i).id))
          setForm(i)('group', {
            id: depositGroup?.id ?? 0,
            number: depositGroup?.number ?? 0,
            name: depositGroup?.name ?? ''
          });
      }
    }
  }, [depositGroups, selectedGroupId]);

  return (
    <S.Form
      style={{
        maxHeight: '80vh',
      }}
      onSubmit={onSubmit}
    >
      <S.Body ref={bodyRef}>
        <S.Grid
          style={{
            gridTemplateColumns: !selectedGroupId && (depositGroupItems.length > 1) ? '1fr 1fr 1fr 1fr 1fr auto' : '1fr 1fr 1fr 1fr auto'
          }}
        >
          {forms.map((_, index) => (
            <>
              <Form.Input
                label="Valor"
                value={getForm(index)('value')}
                onChange={(e) => setForm(index)('value', +(e.target.value || 0))}
                error={messages[index].value}
                onBlur={blurField(index)('value')}
                mask={formatBRL}
                unmask={formatCents}
              />

              <Form.Input.Dropdown
                label="Companhia"
                items={selectCompanies.map(({ id, name }) => ({ id, name }))}
                value={getForm(index)('company.name')}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm(index)('company.name', value);
                  setCompanyFilterField('name')(e);
                }}
                onDebouncedChange={() => {
                  filterCompanies(index);
                }}
                error={messages[index].company}
                onBlur={blurField(index)('company')}
                onFocus={() => {
                  setCompanyFilterField('name')({ target: { value: '' } });
                }}
                onSelected={({ id }) => {
                  setForm(index)('company.id', +(id || 0));
                }}
                loading={companyLoadings[index]}
              />

              <Form.Input.DatePicker
                label="Data do depósito"
                value={maskFieldDate(getForm(index)('date'))}
                onChange={(e) => {
                  setForm(index)('date', unmaskFieldDate(e.target.value));
                  setForm(index)('reference', unmaskFieldDate(e.target.value));
                }}
                error={messages[index].date}
                onBlur={blurField(index)('date')}
              />

              <Form.Input.DatePicker
                label="Data de referência"
                value={maskFieldDate(getForm(index)('reference'))}
                onChange={(e) =>
                  setForm(index)('reference', unmaskFieldDate(e.target.value))
                }
                error={messages[index].reference}
                onBlur={blurField(index)('reference')}
              />


              {!selectedGroupId && (depositGroupItems.length > 1) ? (
                <Form.Input.Dropdown
                  label="Grupo"
                  items={depositGroupItems}
                  value={
                    depositGroupItems.find(({ id }) => id === getForm(index)('group')?.id)?.name ??
                    ''
                  }
                  error={messages[index].group}
                  onBlur={blurField(index)('group')}
                  onSelected={(item) => {
                    setForm(index)('group', item.data);
                  }}
                  disabled={depositGroupItems.length === 1}
                />
              ) : null}

              {index === forms.length - 1 ? (
                <S.ButtonControl type="button" onClick={() => addForm(index)}>
                  <SVG.Icon.Plus width={20} height={20} />
                </S.ButtonControl>
              ) : (
                <S.ButtonControl
                  type="button"
                  onClick={() => removeForm(index)}
                >
                  <SVG.Icon.Trash width={20} height={20} />
                </S.ButtonControl>
              )}
            </>

          ))}
        </S.Grid>
      </S.Body>
      <S.Actions>
        <Form.Button type="button" onClick={clearForm}>
          Limpar
        </Form.Button>
        <Form.Button loading={loading}>Criar</Form.Button>
      </S.Actions>
    </S.Form>
  );
}
